import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Calendar, CheckCircle2, Clock, AlertCircle, ExternalLink, Receipt,
  RotateCcw, DollarSign, Plus, X,
} from "lucide-react";

const STATUS_META = {
  agendado:        { label: "Agendado", color: "#3b82f6", bg: "#dbeafe" },
  confirmado:      { label: "Confirmado", color: "#0891b2", bg: "#cffafe" },
  em_atendimento:  { label: "Em atendimento", color: "#d97706", bg: "#fef3c7" },
  concluido:       { label: "Finalizado", color: "#15803d", bg: "#dcfce7" },
  cancelado:       { label: "Cancelado", color: "#dc2626", bg: "#fee2e2" },
  nao_compareceu:  { label: "Não compareceu", color: "#7c2d12", bg: "#fed7aa" },
  remarcado:       { label: "Remarcado", color: "#a16207", bg: "#fef9c3" },
};

const PAY_LABEL = {
  dinheiro: "Dinheiro", pix: "PIX", credito: "Crédito", debito: "Débito",
  transferencia: "Transferência", boleto: "Boleto", fiado: "Fiado",
  credito_cliente: "Crédito do Cliente",
};

const formatBRL = (n) => `R$ ${Number(n || 0).toFixed(2)}`;

export default function Comandas() {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(searchParams.get("filter") || "abertas");
  const [search, setSearch] = useState("");
  const [days, setDays] = useState(30);
  const [selectedRow, setSelectedRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall("get", `/comandas?days=${days}`);
      setItems(res.data || []);
    } finally {
      setLoading(false);
    }
  }, [apiCall, days]);

  useEffect(() => { load(); }, [load]);

  // Auto-open detail when ?focus=appointment_id is in URL
  useEffect(() => {
    const focusId = searchParams.get("focus");
    if (focusId && items.length > 0) {
      const c = items.find((x) => x.appointment_id === focusId);
      if (c) setSelectedRow(c);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const filtered = useMemo(() => {
    let xs = items;
    if (filter === "abertas") xs = xs.filter((c) => c.is_open);
    else if (filter === "finalizadas") xs = xs.filter((c) => c.status === "concluido" && !c.is_fiado);
    else if (filter === "fiado") xs = xs.filter((c) => c.is_fiado);
    const q = search.trim().toLowerCase();
    if (q) {
      xs = xs.filter((c) =>
        (c.client_name || "").toLowerCase().includes(q) ||
        (c.employee_name || "").toLowerCase().includes(q) ||
        (c.services || []).some((s) => (s || "").toLowerCase().includes(q))
      );
    }
    return xs;
  }, [items, filter, search]);

  const stats = useMemo(() => {
    const abertas = items.filter((c) => c.is_open).length;
    const finalizadas = items.filter((c) => c.status === "concluido").length;
    const fiadoCount = items.filter((c) => c.is_fiado).length;
    const fiadoRemaining = items
      .filter((c) => c.is_fiado)
      .reduce((acc, c) => acc + (c.invoice_remaining || 0), 0);
    const totalFinalizadas = items
      .filter((c) => c.status === "concluido")
      .reduce((acc, c) => acc + (c.paid_amount || 0), 0);
    return { abertas, finalizadas, fiadoCount, fiadoRemaining, totalFinalizadas };
  }, [items]);

  return (
    <div className="space-y-4" data-testid="comandas-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-medium tracking-tight">Comandas</h1>
          <p className="text-xs text-muted-foreground">
            Acompanhe agendamentos em aberto, finalizados e fiado · Últimos {days} dias
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="h-8 px-2 text-xs border border-input rounded-md bg-background"
            data-testid="comandas-days-filter"
          >
            <option value="7">7 dias</option>
            <option value="30">30 dias</option>
            <option value="90">90 dias</option>
            <option value="180">180 dias</option>
            <option value="365">1 ano</option>
            <option value="730">2 anos</option>
            <option value="3650">Histórico completo</option>
          </select>
          <Input
            placeholder="Buscar cliente, funcionário, serviço..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-[260px] text-xs"
            data-testid="comandas-search"
          />
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Em aberto" value={stats.abertas} icon={Clock} color="#d97706" testId="stat-open" />
        <StatCard label="Finalizadas" value={stats.finalizadas} icon={CheckCircle2} color="#15803d" testId="stat-done" />
        <StatCard
          label="Fiado em aberto"
          value={`${formatBRL(stats.fiadoRemaining)} · ${stats.fiadoCount}`}
          icon={AlertCircle}
          color="#b45309"
          testId="stat-fiado"
        />
        <StatCard
          label="Total recebido"
          value={formatBRL(stats.totalFinalizadas)}
          icon={Receipt}
          color="#0891b2"
          testId="stat-total"
        />
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { k: "abertas", l: "Em aberto" },
          { k: "finalizadas", l: "Finalizadas" },
          { k: "fiado", l: "Fiado" },
          { k: "all", l: "Todas" },
        ].map((t) => (
          <Button
            key={t.k}
            size="sm"
            variant={filter === t.k ? "default" : "outline"}
            onClick={() => setFilter(t.k)}
            className="h-7 text-xs"
            data-testid={`comandas-filter-${t.k}`}
          >
            {t.l}
          </Button>
        ))}
      </div>

      {/* List */}
      <Card className="border border-border bg-card shadow-soft rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Carregando comandas...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Nenhuma comanda encontrada.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-3 py-2">Cliente</th>
                  <th className="px-3 py-2">Data</th>
                  <th className="px-3 py-2">Funcionário</th>
                  <th className="px-3 py-2">Serviços</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                  <th className="px-3 py-2">Pagamento</th>
                  <th className="px-3 py-2">Por</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const meta = STATUS_META[c.status] || STATUS_META.agendado;
                  const splits = c.payment_splits || [];
                  return (
                    <tr
                      key={c.appointment_id}
                      className="border-t border-border/40 hover:bg-secondary/30 cursor-pointer"
                      onClick={() => setSelectedRow(c)}
                      data-testid={`comanda-row-${c.appointment_id}`}
                    >
                      <td className="px-3 py-2 font-medium">{c.client_name}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {new Date(c.date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {c.employee_name || "—"}
                        {(c.combined_with || []).length > 0 && (
                          <span className="ml-1 text-[9px] text-amber-700">+{c.combined_with.length}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground max-w-[220px] truncate">
                        {(c.services || []).join(", ") || "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-xs">{formatBRL(c.total_price)}</td>
                      <td className="px-3 py-2 text-xs">
                        {splits.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {splits.map((s, i) => (
                              <span key={i} className="inline-block px-1.5 py-0.5 rounded bg-secondary text-[10px]">
                                {PAY_LABEL[s.payment_method] || s.payment_method} {formatBRL(s.amount)}
                              </span>
                            ))}
                          </div>
                        ) : c.payment_method ? (
                          <span>{PAY_LABEL[c.payment_method] || c.payment_method}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                        {c.is_fiado && c.invoice_status !== "pago" && (
                          <div className="mt-0.5 text-[10px] text-amber-700">
                            Falta {formatBRL(c.invoice_remaining)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">{c.finalized_by || "—"}</td>
                      <td className="px-3 py-2">
                        <Badge
                          variant="outline"
                          style={{ background: meta.bg, color: meta.color, borderColor: "transparent" }}
                          className="text-[10px] uppercase tracking-wider"
                        >
                          {meta.label}
                          {c.reopened_count > 0 && <span className="ml-1">·R{c.reopened_count}</span>}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            const d = new Date(c.date);
                            const iso = d.toISOString().slice(0, 10);
                            navigate(`/agenda?date=${iso}&focus=${c.appointment_id}`);
                          }}
                          className="h-7 px-2 text-[11px]"
                          data-testid={`comanda-goto-agenda-${c.appointment_id}`}
                          title="Abrir na agenda"
                        >
                          <Calendar className="h-3 w-3 mr-1" strokeWidth={1.5} />
                          <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Detail dialog */}
      <ComandaDetailDialog
        open={!!selectedRow}
        comanda={selectedRow}
        onClose={() => setSelectedRow(null)}
        onChanged={() => { setSelectedRow(null); load(); }}
      />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testId }) {
  return (
    <Card className="border border-border bg-card p-3 shadow-soft rounded-md" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-md flex items-center justify-center" style={{ background: `${color}1a` }}>
          <Icon className="h-4 w-4" strokeWidth={1.5} style={{ color }} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="text-lg font-medium tracking-tight">{value}</div>
        </div>
      </div>
    </Card>
  );
}

/* ------------------ Comanda detail / Reabrir / Receber fiado ------------------ */

function ComandaDetailDialog({ open, comanda, onClose, onChanged }) {
  const { apiCall, user } = useAuth();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const isMaster = user?.role === "master";

  useEffect(() => {
    if (!open || !comanda) { setDetail(null); return; }
    let mounted = true;
    setLoadingDetail(true);
    apiCall("get", `/comandas/${comanda.appointment_id}/detail`)
      .then((res) => { if (mounted) setDetail(res.data); })
      .catch(() => { if (mounted) setDetail(null); })
      .finally(() => { if (mounted) setLoadingDetail(false); });
    return () => { mounted = false; };
  }, [open, comanda, apiCall]);

  if (!open || !comanda) return null;

  const meta = STATUS_META[comanda.status] || STATUS_META.agendado;
  const splits = comanda.payment_splits || [];
  const isFinal = comanda.status === "concluido";
  const invoice = (detail?.invoices || []).find((i) => i.invoice_id === comanda.invoice_id) || null;
  const remaining = invoice ? Math.max(0, (invoice.amount || 0) - (invoice.paid_amount || 0)) : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="comanda-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-4 w-4" strokeWidth={1.5} />
            Comanda · {comanda.client_name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {new Date(comanda.date).toLocaleString("pt-BR")} · {comanda.employee_name || "—"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* Status + valor */}
          <div className="flex items-center justify-between p-3 rounded-md border" style={{ background: `${meta.bg}80`, borderColor: meta.color + "33" }}>
            <Badge variant="outline" style={{ background: meta.bg, color: meta.color, borderColor: "transparent" }} className="text-[10px] uppercase tracking-wider">
              {meta.label}{comanda.reopened_count > 0 && ` · Reaberta ${comanda.reopened_count}x`}
            </Badge>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</div>
              <div className="text-lg font-medium" data-testid="detail-total">{formatBRL(comanda.total_price)}</div>
            </div>
          </div>

          {/* Serviços + extras */}
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Serviços</div>
            <div className="text-xs">{(comanda.services || []).join(", ") || "—"}</div>
            {comanda.extras_total > 0 && (
              <div className="text-xs text-amber-700">+ extras: {formatBRL(comanda.extras_total)}</div>
            )}
          </div>

          {/* Combinada */}
          {(detail?.combined_siblings || []).length > 0 && (
            <div className="p-2 rounded bg-amber-50 border border-amber-200 text-xs">
              <div className="font-medium text-amber-900 mb-1">Comanda combinada com {detail.combined_siblings.length} outra(s):</div>
              {detail.combined_siblings.map((s) => (
                <div key={s.appointment_id} className="text-amber-800">
                  · {s.employee_name || "—"} · {formatBRL(s.total_price)} · {(s.services || []).map((x) => x.service_name).join(", ")}
                </div>
              ))}
            </div>
          )}

          {/* Splits de pagamento */}
          {isFinal && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Formas de pagamento</div>
              {splits.length > 0 ? (
                <div className="space-y-0.5">
                  {splits.map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-secondary/50">
                      <span>{PAY_LABEL[s.payment_method] || s.payment_method}</span>
                      <span className="font-medium">{formatBRL(s.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">{PAY_LABEL[comanda.payment_method] || "—"} {formatBRL(comanda.paid_amount)}</div>
              )}
              {comanda.tip_amount > 0 && (
                <div className="flex items-center justify-between text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-900">
                  <span>Gorjeta</span>
                  <span className="font-medium">{formatBRL(comanda.tip_amount)}</span>
                </div>
              )}
            </div>
          )}

          {/* FIADO box */}
          {invoice && (
            <div className="p-3 rounded-md border border-amber-300 bg-amber-50/40 space-y-2" data-testid="fiado-box">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-amber-900 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Fiado
                </div>
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider"
                  style={{ background: invoice.status === "pago" ? "#dcfce7" : invoice.status === "parcial" ? "#fef3c7" : "#fee2e2",
                           color: invoice.status === "pago" ? "#15803d" : invoice.status === "parcial" ? "#a16207" : "#dc2626",
                           borderColor: "transparent" }}>
                  {invoice.status === "pago" ? "Pago" : invoice.status === "parcial" ? "Parcial" : "Pendente"}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Cobrado</div><div className="font-medium">{formatBRL(invoice.amount)}</div></div>
                <div><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Pago</div><div className="font-medium text-emerald-700">{formatBRL(invoice.paid_amount)}</div></div>
                <div><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Falta</div><div className="font-medium text-amber-700">{formatBRL(remaining)}</div></div>
              </div>
              {(invoice.payments || []).length > 0 && (
                <div className="border-t border-amber-200 pt-1.5 space-y-0.5">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Histórico de pagamentos</div>
                  {invoice.payments.map((p) => (
                    <div key={p.payment_id} className="flex justify-between text-[11px]">
                      <span>{new Date(p.paid_at).toLocaleDateString("pt-BR")} · {PAY_LABEL[p.payment_method] || p.payment_method} · {p.received_by_name}</span>
                      <span className="font-medium">{formatBRL(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              {remaining > 0.01 && (
                <Button
                  size="sm"
                  onClick={() => setPayOpen(true)}
                  className="w-full h-8 text-xs"
                  data-testid="receive-fiado-btn"
                >
                  <DollarSign className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                  Receber pagamento ({formatBRL(remaining)})
                </Button>
              )}
            </div>
          )}

          {loadingDetail && <div className="text-xs text-muted-foreground text-center py-2">Carregando detalhes…</div>}
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="detail-close-btn">Fechar</Button>
          {!isFinal && (
            <Button
              size="sm"
              onClick={() => {
                const iso = new Date(comanda.date).toISOString().slice(0, 10);
                onClose();
                navigate(`/agenda?date=${iso}&focus=${comanda.appointment_id}&action=finalize`);
              }}
              data-testid="add-services-finalize-btn"
              style={{ background: "#166534" }}
              className="text-white hover:opacity-90"
            >
              <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
              Adicionar serviços/produtos e finalizar
            </Button>
          )}
          {isFinal && (
            <Button variant="outline" size="sm" onClick={() => setReopenOpen(true)} data-testid="reopen-btn">
              <RotateCcw className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
              Reabrir comanda
            </Button>
          )}
          {isFinal && isMaster && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setVoidOpen(true)}
              className="text-red-700 border-red-300 hover:bg-red-50"
              data-testid="void-sale-btn"
            >
              <X className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
              {comanda.sale_id ? "Estornar venda" : "Estornar comanda (legado)"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      <ReopenDialog
        open={reopenOpen}
        appointmentId={comanda.appointment_id}
        onClose={() => setReopenOpen(false)}
        onDone={() => { setReopenOpen(false); onChanged(); }}
      />

      <ReceivePaymentDialog
        open={payOpen}
        invoice={invoice}
        onClose={() => setPayOpen(false)}
        onDone={() => { setPayOpen(false); onChanged(); }}
      />

      <VoidSaleDialog
        open={voidOpen}
        saleId={comanda.sale_id}
        comanda={comanda}
        onClose={() => setVoidOpen(false)}
        onDone={() => { setVoidOpen(false); onClose(); onChanged(); }}
      />
    </Dialog>
  );
}

/* Sub-dialog: motivo da reabertura */
function ReopenDialog({ open, appointmentId, onClose, onDone }) {
  const { apiCall } = useAuth();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setReason(""); }, [open]);

  const submit = async () => {
    if (!reason.trim()) {
      toast.error("Motivo obrigatório");
      return;
    }
    setBusy(true);
    try {
      await apiCall("post", `/appointments/${appointmentId}/reopen`, {
        status: "em_atendimento",
        notes: reason.trim(),
      });
      toast.success("Comanda reaberta");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao reabrir");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="reopen-dialog">
        <DialogHeader>
          <DialogTitle>Reabrir comanda</DialogTitle>
          <DialogDescription className="text-xs">
            A invoice de fiado (se houver) e as transações registradas serão mantidas.
            Você poderá adicionar itens e novos pagamentos.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Motivo da reabertura *</Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex: Cliente quer adicionar produto / Trocar forma de pagamento / Corrigir valor…"
            rows={3}
            data-testid="reopen-reason"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button size="sm" onClick={submit} disabled={busy || !reason.trim()} data-testid="reopen-confirm">
            {busy ? "Reabrindo…" : "Reabrir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Sub-dialog: receber pagamento de fiado */
function ReceivePaymentDialog({ open, invoice, onClose, onDone }) {
  const { apiCall } = useAuth();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("dinheiro");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const remaining = invoice ? Math.max(0, (invoice.amount || 0) - (invoice.paid_amount || 0)) : 0;

  useEffect(() => {
    if (open && invoice) {
      setAmount(remaining.toFixed(2));
      setMethod("dinheiro");
      setNotes("");
    }
  }, [open, invoice, remaining]);

  if (!invoice) return null;

  const submit = async () => {
    const v = parseFloat(amount) || 0;
    if (v <= 0) {
      toast.error("Valor inválido");
      return;
    }
    if (v > remaining + 0.02) {
      toast.error(`Valor excede o saldo devedor (${formatBRL(remaining)})`);
      return;
    }
    setBusy(true);
    try {
      await apiCall("post", `/invoices/${invoice.invoice_id}/payments`, {
        amount: v,
        payment_method: method,
        notes: notes.trim() || null,
      });
      toast.success(`Pagamento de ${formatBRL(v)} registrado`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registrar pagamento");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="receive-payment-dialog">
        <DialogHeader>
          <DialogTitle>Receber pagamento de fiado</DialogTitle>
          <DialogDescription className="text-xs">
            {invoice.client_name} · Saldo devedor: <b>{formatBRL(remaining)}</b>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Valor *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max={remaining}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="pay-amount"
            />
          </div>
          <div>
            <Label className="text-xs">Forma de pagamento *</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dinheiro">Dinheiro</SelectItem>
                <SelectItem value="pix">PIX</SelectItem>
                <SelectItem value="credito">Crédito</SelectItem>
                <SelectItem value="debito">Débito</SelectItem>
                <SelectItem value="transferencia">Transferência</SelectItem>
                <SelectItem value="boleto">Boleto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Observação (opcional)</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: cliente quitou após combinar via WhatsApp"
              data-testid="pay-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button size="sm" onClick={submit} disabled={busy || !amount} data-testid="pay-confirm">
            {busy ? "Registrando…" : "Registrar pagamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Sub-dialog: estornar venda (Fase 4 do ledger imutável) */
function VoidSaleDialog({ open, saleId, comanda, onClose, onDone }) {
  const { apiCall } = useAuth();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (open) { setReason(""); setConfirmText(""); }
  }, [open]);

  const submit = async () => {
    if (reason.trim().length < 5) {
      toast.error("Informe um motivo com pelo menos 5 caracteres");
      return;
    }
    if (confirmText.trim().toUpperCase() !== "ESTORNAR") {
      toast.error("Digite ESTORNAR para confirmar");
      return;
    }
    setBusy(true);
    try {
      let res;
      if (saleId) {
        res = await apiCall("post", `/sales/${saleId}/void`, { reason: reason.trim() });
      } else {
        // Legacy path: comanda concluída antes da Fase 2 (sem sale_id)
        res = await apiCall("post", `/appointments/${comanda.appointment_id}/void-legacy`, { reason: reason.trim() });
      }
      const d = res.data || {};
      const parts = [];
      if (d.credit_refunded) parts.push(`crédito devolvido: R$ ${Number(d.credit_refunded).toFixed(2)}`);
      if (d.deposit_reversed) parts.push(`depósito estornado: R$ ${Number(d.deposit_reversed).toFixed(2)}`);
      if (d.reversal_transaction_ids?.length) parts.push(`${d.reversal_transaction_ids.length} lançamento(s) estornado(s)`);
      if (d.invoices_cancelled) parts.push(`${d.invoices_cancelled} fiado(s) cancelado(s)`);
      toast.success(`Comanda estornada · ${parts.join(" · ") || "ok"}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao estornar");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="void-sale-dialog">
        <DialogHeader>
          <DialogTitle className="text-red-700">{saleId ? "Estornar venda" : "Estornar comanda (legado)"}</DialogTitle>
          <DialogDescription className="text-xs">
            {saleId ? (
              <>Reverte de forma <b>imutável</b> as transactions financeiras, devolve crédito consumido ao cliente
              e reabre a comanda. A venda NÃO é apagada — fica marcada como <code>voided</code>.</>
            ) : (
              <>Esta comanda foi finalizada <b>antes</b> do módulo de vendas imutáveis (Fase 2). O estorno
              legado reverte as transactions vinculadas via appointment_id, cancela invoices e reabre a comanda.
              Todas as ações ficam em <code>audit_log</code>.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs p-2 rounded bg-red-50 border border-red-200 text-red-900">
            <div className="font-medium mb-0.5">{comanda?.client_name} · {formatBRL(comanda?.total_price)}</div>
            <div className="text-[11px]">
              {saleId ? <>Sale ID: <code>{saleId?.slice(-8)}</code></> : <>Appointment: <code>{comanda?.appointment_id?.slice(-8)}</code> (comanda legada, sem sale_id)</>}
            </div>
          </div>
          <div>
            <Label className="text-xs">Motivo do estorno <span className="text-red-600">*</span></Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: cliente devolveu produto, atendimento não realizado, erro de digitação…"
              data-testid="void-reason"
            />
            <div className="text-[10px] text-muted-foreground mt-1">Mínimo 5 caracteres · fica registrado no audit_log</div>
          </div>
          <div>
            <Label className="text-xs">Digite <b>ESTORNAR</b> para confirmar</Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="ESTORNAR"
              data-testid="void-confirm-text"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={busy || reason.trim().length < 5 || confirmText.trim().toUpperCase() !== "ESTORNAR"}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="void-confirm-btn"
          >
            {busy ? "Estornando…" : "Confirmar estorno"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

